import React from 'react';
import type { CardProps, CardButtonProps, ButtonInputProps } from './types';
import styles from './index.module.less';
import classnames from 'classnames';
import { Color, FontType } from '../../../core/enums';
import withEventTracking from '../../../utilities/withEventTracking';
import { Typography } from '../../archived/typography';
import Element from '../../../core/Element';

export const getID = (
  prefix?: string,
  qeID?: string,
  color?: string
): string => {
  return `${prefix}_ColorPicker_${qeID ? qeID : color}`;
};

export const getButtonRoleElementTheme = (
  forceColumn: boolean,
  isLast: boolean
) => ({
  marginBottom: forceColumn && !isLast ? 10 : 0,
});

export const getTypographyTheme = (
  isSelected: boolean
): Record<string, number | string> => ({
  paddingTop: 5,
  paddingBottom: 5,
  color: isSelected ? Color.BrightBlue : Color.Gray05,
  marginTop: 0,
  marginBottom: 0,
});

export const getNameComponent = (
  name: string,
  isSelected: boolean
): React.ReactElement => {
  const NameComponent = isSelected ? 'b' : 'span';
  return <NameComponent>{name}</NameComponent>;
};

export const getPaddingTheme = (
  isExpanded: boolean
): Record<string, number> => ({
  padding: isExpanded ? 10 : 5,
});

const ButtonInput = ({
  name,
  qeId = '',
  onClick,
  isSelected,
  forceColumn,
  isLast,
  children,
}: ButtonInputProps): React.ReactElement => {
  return (
    <>
      <input
        type="radio"
        id={qeId}
        data-qe-id={`${qeId}_input`}
        className={`${styles.cardButtonInput} ${styles.hidden} ${styles.primaryColorPickerBtn}`}
        name={name}
        checked={isSelected}
        onClick={onClick}
      />
      <Element
        Component="label"
        htmlFor={qeId}
        qeId={`${qeId}_label`}
        className={classnames(styles.cardBorder, {
          [styles.cardButtonLabel]: !isSelected,
        })}
        theme={getButtonRoleElementTheme(forceColumn, isLast)}
      >
        {children}
      </Element>
    </>
  );
};

// Card with a card color and card name like "Expanded View w/forceColumn".
const CardButton = ({
  name,
  qeId,
  containerQeID,
  handler,
  onClick,
  id,
  isSelected,
  forceColumn,
  isLast,
  children,
}: CardButtonProps): React.ReactElement => {
  if (containerQeID) {
    return (
      <ButtonInput
        name={`${containerQeID}:ColorPicker`}
        qeId={getID(containerQeID, qeId, name)}
        onClick={handler || onClick}
        isSelected={isSelected}
        forceColumn={forceColumn}
        isLast={isLast}
      >
        {children}
      </ButtonInput>
    );
  } else {
    // In the next major release this should be removed, it is only here for when no containerQeID is provided.
    // In the next major release qeID should be mandatory
    return (
      <Element
        role="button"
        onClick={handler || onClick}
        qeId={getID('Button', qeId, id)}
        tabIndex={isSelected ? 0 : -1}
        theme={getButtonRoleElementTheme(forceColumn, isLast)}
        className={`${styles.cardButton} ${styles.cardBorder}`}
      >
        {children}
      </Element>
    );
  }
};

// The 'handler' prop in Card performs the same function as 'onClick' and should be removed in the next major release.
const Card = ({
  colorValueOne,
  colorValueTwo,
  name,
  id,
  qeId,
  onClick,
  handler,
  isLast,
  forceColumn,
  isExpanded,
  elementClassNames,
  containerQeID,
  isSelected,
}: CardProps): React.ReactElement => {
  const sphereInlineStyle = {
    background: `linear-gradient(180deg, ${colorValueOne} 0%, ${colorValueTwo} 100%)`,
  };

  return (
    <CardButton
      name={name}
      qeId={qeId}
      containerQeID={containerQeID}
      handler={handler}
      onClick={onClick}
      id={id}
      isSelected={isSelected}
      forceColumn={forceColumn}
      isLast={isLast}
    >
      <Element className={elementClassNames}>
        <Element theme={getPaddingTheme(isExpanded)}>
          <Element className={styles.contentFlexWrapper}>
            <div
              className={
                isExpanded ? styles.largerColorSphere : styles.colorSphere
              }
              style={sphereInlineStyle}
            />
            <Typography
              theme={{
                ...getTypographyTheme(isSelected),
                paddingLeft: 5,
              }}
              $size={12}
              $lineHeight={14}
              $family={FontType.HelveticaRegular}
            >
              {getNameComponent(name, isSelected)}
            </Typography>
          </Element>
        </Element>
      </Element>
    </CardButton>
  );
};

export default withEventTracking<CardProps>(Card);
